import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SashaAvatar from "../components/SashaAvatar";
import {
  Mic,
  MessageSquare,
  ClipboardList,
  LifeBuoy,
  ArrowRight,
  Search,
  Users,
  Zap,
  Sparkles,
  UserCheck,
  MapPin,
  TrendingUp,
} from "lucide-react";

const DESTINATIONS = [
  {
    title: "Amalfi Coast",
    sub: "Italy · 5 nights",
    img: "https://images.unsplash.com/photo-1583844056361-4418a8f2a985?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMGl0YWx5fGVufDB8fHx8MTc3NzE2Njk3OXww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Swiss Alps",
    sub: "Verbier · 4 nights",
    img: "https://images.unsplash.com/photo-1614703428261-09e1ac47199a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxzd2lzcyUyMGFscHMlMjBzbm93JTIwbW91bnRhaW58ZW58MHx8fHwxNzc3MTY2OTc5fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Private Estate",
    sub: "Janté Host Network",
    img: "https://images.unsplash.com/photo-1776763019060-fa0663574ae6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMHJlc29ydCUyMGludGVyaW9yfGVufDB8fHx8MTc3NzE2Njk3OXww&ixlib=rb-4.1.0&q=85",
  },
];

const HOW_IT_WORKS = [
  {
    icon: MessageSquare,
    title: "Tell Sasha where you're going and what matters.",
    body: "A conversation, not a form. Sasha listens for the details that shape the trip.",
  },
  {
    icon: ClipboardList,
    title: "Janté handles the planning, booking, and coordination.",
    body: "Flights, stays, transfers, experiences — all sequenced, timed, and reserved.",
  },
  {
    icon: LifeBuoy,
    title: "You get one point of contact from start to finish.",
    body: "Before the trip. During. After. One name in your inbox.",
  },
];

const WHY_JANTE = [
  {
    icon: Zap,
    title: "Faster planning without losing the luxury feel",
    body: "The speed of a modern tool with the polish of a five-star hotel concierge.",
  },
  {
    icon: LifeBuoy,
    title: "Live support when the trip needs nuance",
    body: "Complex logistics, last-minute changes, special moments — a human is always a message away.",
  },
  {
    icon: UserCheck,
    title: "Your assigned concierge assistant, not a booking tool",
    body: "We are built around you — preferences, family, rhythm — not a generic search box.",
  },
  {
    icon: Sparkles,
    title: "With you before, during, and back home again",
    body: "Reservations confirmed. Drivers on time. Restaurants briefed. We stay on the line.",
  },
];

export default function Landing() {
  const [showFloat, setShowFloat] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowFloat(window.scrollY > Math.min(420, window.innerHeight * 0.6));
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero — luxury + proof first */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="overline fade-up" data-testid="hero-overline">
            <span className="text-[var(--brand-red)]">Janté Travel Concierges</span>
            <span className="mx-2 opacity-60">·</span>
            Your assigned concierge
          </div>

          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl text-[var(--emerald)] leading-[1.05] mt-6 fade-up fade-up-delay-1"
            data-testid="hero-headline"
          >
            Luxury travel,<br />
            <em className="not-italic text-[var(--gold)]">designed</em> around one point of contact.
          </h1>

          <p
            className="mt-8 text-lg leading-relaxed text-[var(--ink-2)] max-w-xl fade-up fade-up-delay-2"
            data-testid="hero-subhead"
          >
            Sasha is Janté's concierge for flights, stays, transfers, and experiences, with live
            support. Built for travelers who want speed, discretion, and a seamless way to plan travel.
          </p>

          {/* Proof line */}
          <div
            className="mt-8 inline-flex items-start gap-3 border-l-2 border-[var(--gold)] pl-5 py-2 fade-up fade-up-delay-2 max-w-xl"
            data-testid="hero-proof"
          >
            <TrendingUp size={18} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
            <p className="text-sm text-[var(--ink-2)] leading-relaxed">
              We've already validated demand with{" "}
              <strong className="text-[var(--emerald)]">$160K booked in 38 days</strong> through a
              single London property proof of concept.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-delay-3">
            <Link to="/chat" className="btn-primary" data-testid="hero-talk-to-sasha-btn">
              <Mic size={16} strokeWidth={1.5} /> Talk to Sasha
            </Link>
            <Link to="/join" className="btn-ghost" data-testid="hero-join-btn">
              Join JTL <ArrowRight size={14} strokeWidth={1.25} />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 fade-up fade-up-delay-1">
          <SashaAvatar active size="xl" showName={false} />
          <div className="text-center mt-6">
            <div className="overline">your concierge</div>
            <div className="font-display text-3xl text-[var(--emerald)] mt-1">Sasha</div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24"
        data-testid="how-it-works"
      >
        <div className="max-w-2xl">
          <div className="overline">How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-4 leading-tight">
            Three steps.<br />
            <em className="text-[var(--gold)] not-italic">One</em> person on the other end.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-14">
          {HOW_IT_WORKS.map((s, i) => (
            <div
              key={s.title}
              className="relative"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 border border-[var(--gold)] flex items-center justify-center font-display text-2xl text-[var(--gold)]"
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <s.icon size={18} strokeWidth={1.25} className="text-[var(--emerald)]/50" />
              </div>
              <h3 className="font-display text-2xl text-[var(--emerald)] mt-6 leading-snug">
                {s.title}
              </h3>
              <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Janté */}
      <section className="bg-[var(--ivory-2)] py-20 lg:py-24 px-6 lg:px-12" data-testid="why-jante">
        <div className="max-w-7xl mx-auto">
          <div className="overline">Why Janté</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-4 max-w-3xl leading-tight">
            The feel of an old-world concierge, <em className="text-[var(--gold)] not-italic">with the speed</em> of right now.
          </h2>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 mt-14 max-w-5xl">
            {WHY_JANTE.map((w) => (
              <div
                key={w.title}
                className="flex gap-5"
                data-testid={`why-${w.title.split(" ")[0].toLowerCase()}`}
              >
                <div className="shrink-0 w-11 h-11 border border-[var(--gold)] flex items-center justify-center text-[var(--gold)]">
                  <w.icon size={18} strokeWidth={1.25} />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-[var(--emerald)] leading-tight">
                    {w.title}
                  </h3>
                  <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{w.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-20 lg:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="overline">Curated journeys</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-4 max-w-2xl leading-tight">
            Where shall we take you?
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {DESTINATIONS.map((d, i) => (
              <article
                key={d.title}
                className="group relative aspect-[4/5] overflow-hidden bg-white"
                data-testid={`destination-card-${i}`}
              >
                <img
                  src={d.img}
                  alt={d.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--emerald)]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-[var(--ivory)]">
                  <div className="overline text-[var(--gold)]">{d.sub}</div>
                  <div className="font-display text-3xl mt-1">{d.title}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Tenant Thrive — quiet mention, lower on the page */}
      <section
        className="max-w-5xl mx-auto px-6 lg:px-12 py-16"
        data-testid="landing-mission-strip"
      >
        <div className="lux-card flex flex-col md:flex-row items-start md:items-center gap-6">
          <MapPin size={22} strokeWidth={1.25} className="text-[var(--brand-red)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="overline text-[var(--brand-red)]">Janté Tenant Thrive</div>
            <p
              className="mt-2 italic text-[var(--brand-red)]"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: "1.35rem",
                fontWeight: 500,
                letterSpacing: "0.04em",
              }}
            >
              Vacation with the Mission in Mind
            </p>
            <p className="mt-2 text-[var(--ink-2)] leading-relaxed">
              A portion of every vacation dollar is redirected back into the local
              destinations we travel to — dedicated to safe, affordable housing.
            </p>
          </div>
          <Link
            to="/tenant-thrive"
            className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 border border-[var(--emerald)] text-[var(--emerald)] hover:bg-[var(--emerald)] hover:text-[var(--ivory)] transition-colors shrink-0"
            data-testid="landing-mission-cta"
          >
            Learn the mission
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-20 text-center">
        <div className="overline">Begin tonight</div>
        <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-4">
          Speak. We'll start designing your trip.
        </h2>
        <p className="mt-6 text-lg text-[var(--ink-2)] max-w-2xl mx-auto">
          Sasha listens, plans, and quietly hands you to a live concierge whenever the moment
          calls for it.
        </p>
        <div className="mt-10">
          <Link to="/chat" className="btn-primary" data-testid="footer-cta-btn">
            <Mic size={16} strokeWidth={1.5} /> Talk to Sasha
          </Link>
        </div>
      </section>

      {/* Spacer so the floating split-CTA doesn't overlap the footer */}
      <div aria-hidden="true" className="h-24" />

      {/* Floating split-screen CTA — pinned bottom of the landing, fades in on scroll past hero */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-2rem)] max-w-2xl transition-all duration-500 ${
          showFloat ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        data-testid="floating-split-cta"
      >
        <div className="grid grid-cols-2 bg-[var(--ivory)] text-[var(--emerald)] shadow-[0_24px_56px_-24px_rgba(10,54,34,0.35)] backdrop-blur-md border border-[var(--gold)]/40 overflow-hidden">
          <Link
            to="/stays"
            className="group flex items-center justify-center gap-2.5 py-4 px-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors border-r border-[var(--gold)]/40"
            data-testid="float-cta-search-properties"
          >
            <Search size={14} strokeWidth={1.5} className="text-[var(--gold)] group-hover:text-[var(--emerald)] transition-colors" />
            Search Properties
          </Link>
          <Link
            to="/hosts"
            className="group flex items-center justify-center gap-2.5 py-4 px-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors"
            data-testid="float-cta-meet-hosts"
          >
            <Users size={14} strokeWidth={1.5} className="text-[var(--gold)] group-hover:text-[var(--emerald)] transition-colors" />
            Meet Hosts
          </Link>
        </div>
      </div>
    </div>
  );
}
